// src/lib/mystara/oracle-deck.ts
// The Self-Alignment Card Deck - 24 Cards for Transformation

import type { OracleCard, CardSuit } from '../../types/mystara';

// ============================================
// IDENTITY CARDS (6) - Who You're Becoming
// ============================================

const IDENTITY_CARDS: OracleCard[] = [
  {
    id: 'identity-01-the-becoming',
    name: 'The Becoming',
    suit: 'identity',
    suitOrder: 1,
    coreMeaning: 'You are in the process of transformation. The person you\'re becoming is already within you, waiting to emerge.',
    lightInterpretation: 'Trust the process. You don\'t need to have it all figured out. The caterpillar doesn\'t know what the butterfly looks like, but it becomes one anyway. Your transformation is already in motion.',
    shadowInterpretation: 'Are you rushing the process? Trying to skip steps? Real becoming takes time. Don\'t mistake impatience for ambition.',
    reflectionQuestion: 'What version of yourself is trying to emerge right now?',
    keywords: ['transformation', 'potential', 'emergence', 'trust', 'process'],
    visualDescription: 'A figure mid-transformation, half silhouette, half luminous. Soft gradients of dawn colors. A chrysalis motif. Feeling of emergence without urgency.',
    affirmation: 'I am becoming who I\'m meant to be, one moment at a time.'
  },
  {
    id: 'identity-02-the-authentic',
    name: 'The Authentic',
    suit: 'identity',
    suitOrder: 2,
    coreMeaning: 'Your power lies in being fully yourself. The masks can come off.',
    lightInterpretation: 'There\'s something you\'ve been hiding or dimming to fit in. This card is permission to stop. The right people and opportunities will find you when you\'re actually you.',
    shadowInterpretation: 'Are you performing authenticity? Sometimes "being real" becomes its own mask. True authenticity doesn\'t announce itself.',
    reflectionQuestion: 'Where are you still performing instead of just being?',
    keywords: ['truth', 'self-expression', 'honesty', 'presence', 'alignment'],
    visualDescription: 'A mirror reflecting not the surface, but something luminous beneath. Layers peeling away to reveal light. Soft, warm tones.',
    affirmation: 'I am safe to be exactly who I am.'
  },
  {
    id: 'identity-03-the-sovereign',
    name: 'The Sovereign',
    suit: 'identity',
    suitOrder: 3,
    coreMeaning: 'You are the authority of your own life. No one else gets to define you.',
    lightInterpretation: 'It\'s time to stop waiting for permission. Stop looking for external validation. You are the queen of your own realm. Make the decision. Set the boundary. Choose yourself.',
    shadowInterpretation: 'Sovereignty doesn\'t mean isolation or refusing all input. Are you conflating independence with pushing everyone away?',
    reflectionQuestion: 'Where have you been giving your power away? What would it look like to take it back?',
    keywords: ['power', 'authority', 'boundaries', 'self-trust', 'leadership'],
    visualDescription: 'A figure seated on a simple throne, not ornate but grounded. Crown is subtle, almost a halo. Posture is relaxed confidence, not rigid authority.',
    affirmation: 'I am the authority of my own life.'
  },
  {
    id: 'identity-04-the-worthy',
    name: 'The Worthy',
    suit: 'identity',
    suitOrder: 4,
    coreMeaning: 'You don\'t have to earn your worth. It was never in question.',
    lightInterpretation: 'Whatever you\'re wanting—love, success, abundance, joy—you\'re allowed to have it. Not because you\'ve worked hard enough or suffered enough or proved yourself. Simply because you exist.',
    shadowInterpretation: 'Are you trying to think your way into worthiness? Affirmations won\'t work if there\'s a part of you that doesn\'t believe them. What needs to be felt, not just said?',
    reflectionQuestion: 'If you truly believed you were worthy, what would you do differently today?',
    keywords: ['worthiness', 'deserving', 'self-acceptance', 'enoughness', 'receiving'],
    visualDescription: 'An open hand, palm up, receiving light. No grasping, no striving. Soft gold and rose tones. Feeling of ease and allowing.',
    affirmation: 'I am worthy of everything I desire, simply because I am.'
  },
  {
    id: 'identity-05-the-magnetic',
    name: 'The Magnetic',
    suit: 'identity',
    suitOrder: 5,
    coreMeaning: 'You attract what you embody. Become it first.',
    lightInterpretation: 'Stop chasing. Start being. When you fully embody the energy of what you want—the confidence, the abundance, the love—it has no choice but to find you. You become a magnet.',
    shadowInterpretation: 'Magnetism isn\'t manipulation. Are you trying to "be" a certain way to get something, rather than genuinely transforming? The energy knows the difference.',
    reflectionQuestion: 'What energy are you putting out right now? Is it what you want to attract?',
    keywords: ['attraction', 'embodiment', 'energy', 'alignment', 'presence'],
    visualDescription: 'A glowing figure with subtle magnetic field lines around them. Things floating gently toward them. Colors are warm and inviting.',
    affirmation: 'I am a magnet for everything aligned with my highest good.'
  },
  {
    id: 'identity-06-the-visible',
    name: 'The Visible',
    suit: 'identity',
    suitOrder: 6,
    coreMeaning: 'You\'re ready to be seen. Step into the light.',
    lightInterpretation: 'Hiding kept you safe once. It doesn\'t serve you anymore. Your voice, your work, your presence—someone needs it. The world can\'t receive what you\'re not willing to share.',
    shadowInterpretation: 'Visibility for its own sake is empty. Are you seeking to be seen or seeking to contribute? One feeds the ego, the other feeds the soul.',
    reflectionQuestion: 'What would you create or share if you weren\'t afraid of being seen?',
    keywords: ['visibility', 'courage', 'expression', 'sharing', 'impact'],
    visualDescription: 'A figure stepping from shadow into gentle spotlight. Not harsh or exposing, but warm and welcoming. Feeling of being held, not judged.',
    affirmation: 'I am ready to be seen for who I truly am.'
  }
];

// ============================================
// SHADOW CARDS (6) - What Needs Attention
// ============================================

const SHADOW_CARDS: OracleCard[] = [
  {
    id: 'shadow-01-the-resistance',
    name: 'The Resistance',
    suit: 'shadow',
    suitOrder: 1,
    coreMeaning: 'Notice where you\'re pushing back. There\'s information there.',
    lightInterpretation: 'You\'re resisting something—an idea, an action, a truth. Resistance isn\'t bad; it\'s data. It\'s showing you where your edge is, where growth wants to happen. Lean into the inquiry.',
    shadowInterpretation: 'Not all resistance should be overcome. Sometimes it\'s wisdom protecting you from something wrong for you. Which is this?',
    reflectionQuestion: 'What are you resisting right now, and what might that resistance be protecting?',
    keywords: ['resistance', 'avoidance', 'edge', 'growth', 'inquiry'],
    visualDescription: 'A wall or barrier with light leaking through the cracks. What\'s behind it? Mysterious but not threatening.',
    affirmation: 'I explore my resistance with curiosity, not judgment.'
  },
  {
    id: 'shadow-02-the-old-story',
    name: 'The Old Story',
    suit: 'shadow',
    suitOrder: 2,
    coreMeaning: 'You\'re living from an outdated narrative. Time for a new one.',
    lightInterpretation: 'There\'s a story you\'ve been telling about yourself—maybe for years, maybe since childhood—that isn\'t true anymore. Or maybe never was. It\'s shaping your reality even though it\'s expired. Name it.',
    shadowInterpretation: 'Changing stories doesn\'t mean denying your past or pretending pain didn\'t happen. What\'s the difference between a new story and denial?',
    reflectionQuestion: 'What old story about yourself is ready to be retired?',
    keywords: ['narrative', 'belief', 'identity', 'rewriting', 'truth'],
    visualDescription: 'An old book closing as a new one opens. The stories we tell. Transition imagery.',
    affirmation: 'I release old stories and write new truths.'
  },
  {
    id: 'shadow-03-the-unworthiness',
    name: 'The Unworthiness',
    suit: 'shadow',
    suitOrder: 3,
    coreMeaning: 'Underneath the surface story, there\'s a worthiness wound.',
    lightInterpretation: 'Whatever you\'re struggling with right now—scratch beneath and you\'ll likely find unworthiness. "I\'m not good enough, smart enough, lovable enough." This is the real work. Everything else is symptoms.',
    shadowInterpretation: 'Affirmations alone won\'t heal this. Unworthiness often lives in the body, not the mind. What does it need—to be felt, to be witnessed, to be grieved?',
    reflectionQuestion: 'Where is unworthiness secretly running the show in your life?',
    keywords: ['worthiness', 'self-worth', 'enoughness', 'core wound', 'healing'],
    visualDescription: 'A dim light in the center of the chest waiting to be recognized and brightened. Hidden but not lost.',
    affirmation: 'I am worthy, even when I don\'t feel it.'
  },
  {
    id: 'shadow-04-the-control',
    name: 'The Control',
    suit: 'shadow',
    suitOrder: 4,
    coreMeaning: 'Your grip is too tight. What would happen if you loosened it?',
    lightInterpretation: 'You\'re trying to control the outcome, the timing, the how. But control is an illusion that exhausts you. What if you did your part and released the rest? What might become possible?',
    shadowInterpretation: 'Some things do need your attention and direction. This isn\'t about being passive. It\'s about knowing what\'s yours to control and what isn\'t.',
    reflectionQuestion: 'What are you trying to control that isn\'t actually yours to control?',
    keywords: ['control', 'grip', 'surrender', 'trust', 'release'],
    visualDescription: 'Hands gripping tightly, then slowly opening. The relief of release. Tension dissolving.',
    affirmation: 'I release my need to control what isn\'t mine to control.'
  },
  {
    id: 'shadow-05-the-hiding',
    name: 'The Hiding',
    suit: 'shadow',
    suitOrder: 5,
    coreMeaning: 'You\'re making yourself small. Who told you that was safer?',
    lightInterpretation: 'There\'s a part of you—your voice, your power, your truth, your desire—that you\'re hiding. Once, this protected you. Does it still? Or is it just habit now?',
    shadowInterpretation: 'Sometimes hiding is wisdom, not wound. Are you hiding from something genuinely dangerous, or from the discomfort of being seen?',
    reflectionQuestion: 'What are you hiding, and what would happen if you stopped?',
    keywords: ['hiding', 'smallness', 'visibility', 'protection', 'fear'],
    visualDescription: 'A figure partially in shadow, partially in light. The invitation to step forward. Gentle, not exposing.',
    affirmation: 'I am safe to be seen.'
  },
  {
    id: 'shadow-06-the-perfectionism',
    name: 'The Perfectionism',
    suit: 'shadow',
    suitOrder: 6,
    coreMeaning: 'Perfectionism isn\'t excellence. It\'s fear dressed up as high standards.',
    lightInterpretation: 'Your perfectionism looks like ambition, but it\'s actually protection. If you never finish, you can\'t be judged. If you don\'t try, you can\'t fail. But perfectionism is a prison with a lock on the inside.',
    shadowInterpretation: 'Some things do deserve your best effort and attention to detail. Excellence isn\'t perfectionism. What\'s the felt difference for you?',
    reflectionQuestion: 'What would you do or create if you weren\'t afraid of doing it imperfectly?',
    keywords: ['perfectionism', 'fear', 'paralysis', 'excellence', 'freedom'],
    visualDescription: 'Perfect cage opening to reveal imperfect but beautiful freedom. The relief of good enough.',
    affirmation: 'I release perfectionism and embrace my beautiful, imperfect humanity.'
  }
];

// ============================================
// DIRECTION CARDS (6) - Guidance for Your Path
// ============================================

const DIRECTION_CARDS: OracleCard[] = [
  {
    id: 'direction-01-trust-the-timing',
    name: 'Trust the Timing',
    suit: 'direction',
    suitOrder: 1,
    coreMeaning: 'It\'s not late. It\'s not early. It\'s exactly when it should be.',
    lightInterpretation: 'Your anxiety about timing is creating more delay than the timing itself. What\'s meant for you won\'t miss you. Relax into the when. Focus on the who you\'re becoming.',
    shadowInterpretation: 'Trust in timing doesn\'t mean passivity. You still have to show up, do the work, make the moves. Divine timing meets human effort.',
    reflectionQuestion: 'Where is impatience about timing creating unnecessary suffering?',
    keywords: ['timing', 'patience', 'trust', 'surrender', 'divine order'],
    visualDescription: 'A clock with no hands, or hands moving in a spiral. The peace of timelessness. Cosmic and serene.',
    affirmation: 'I trust that everything is unfolding in perfect timing.'
  },
  {
    id: 'direction-02-feel-it-first',
    name: 'Feel It First',
    suit: 'direction',
    suitOrder: 2,
    coreMeaning: 'The feeling creates the reality, not the other way around.',
    lightInterpretation: 'Stop waiting to feel abundant until you have money. Stop waiting to feel loved until you have the relationship. The feeling is the attractor. Feel it first, fully, now. Reality follows.',
    shadowInterpretation: 'Feeling isn\'t faking or forcing. If you can\'t access the feeling genuinely, there\'s a block to explore—not a performance to perfect.',
    reflectionQuestion: 'What feeling are you waiting to have that you could choose to feel now?',
    keywords: ['feeling', 'emotion', 'embodiment', 'creation', 'attraction'],
    visualDescription: 'A figure with hand on heart, radiating the energy outward. Feeling as creation. Warm, emanating light.',
    affirmation: 'I feel it now. The feeling is the magnet.'
  },
  {
    id: 'direction-03-your-desires-are-valid',
    name: 'Your Desires are Valid',
    suit: 'direction',
    suitOrder: 3,
    coreMeaning: 'What you want isn\'t too much, too selfish, or too unrealistic.',
    lightInterpretation: 'Someone convinced you that wanting was wrong, that your desires were excessive or inappropriate. They were wrong. Your desires are data. They\'re showing you who you\'re meant to become.',
    shadowInterpretation: 'Valid doesn\'t mean unexamined. Are these actually your desires, or someone else\'s expectations you\'ve internalized?',
    reflectionQuestion: 'What desire have you been judging or hiding because you think it\'s too much?',
    keywords: ['desire', 'validation', 'wanting', 'worthiness', 'permission'],
    visualDescription: 'A glowing wish or star being held close to the heart. Precious. Valid. Honored.',
    affirmation: 'My desires are valid, and I honor them.'
  },
  {
    id: 'direction-04-youre-already-ready',
    name: 'You\'re Already Ready',
    suit: 'direction',
    suitOrder: 4,
    coreMeaning: 'The readiness you\'re waiting for? You already have it.',
    lightInterpretation: 'You keep preparing, researching, getting "ready." But readiness is often a disguise for fear. You\'re ready. You\'ve been ready. The thing you think you need more of? You have enough.',
    shadowInterpretation: 'Ready doesn\'t mean perfect or guaranteed to succeed. It means capable of handling what comes. Are you confusing readiness with certainty?',
    reflectionQuestion: 'What have you been telling yourself you\'re not ready for that you actually are?',
    keywords: ['readiness', 'capability', 'courage', 'action', 'now'],
    visualDescription: 'A figure at a starting line, realizing they can go. The moment of recognition. Empowering.',
    affirmation: 'I am ready now. I don\'t need to wait any longer.'
  },
  {
    id: 'direction-05-action-over-perfection',
    name: 'Action Over Perfection',
    suit: 'direction',
    suitOrder: 5,
    coreMeaning: 'Done is better than perfect. Move now, refine later.',
    lightInterpretation: 'Perfectionism is fear in fancy clothes. It keeps you safe from failure by keeping you safe from trying. Lower the bar. Ship the thing. Send the message. Imperfect action beats perfect inaction.',
    shadowInterpretation: 'Action over perfection doesn\'t mean carelessness or half-effort. Some things deserve your best. Which is this?',
    reflectionQuestion: 'What are you not doing because you\'re waiting until you can do it perfectly?',
    keywords: ['action', 'imperfection', 'courage', 'progress', 'movement'],
    visualDescription: 'A rough sketch being made confidently. Beauty in imperfection. Progress over polish.',
    affirmation: 'I choose progress over perfection.'
  },
  {
    id: 'direction-06-the-path-is-made-by-walking',
    name: 'The Path is Made by Walking',
    suit: 'direction',
    suitOrder: 6,
    coreMeaning: 'You won\'t find the path by thinking about it. You make it by moving.',
    lightInterpretation: 'Stop waiting for clarity before you act. Take the step. Any step. The path reveals itself through movement, not meditation. You can\'t think your way to a new life—you have to walk there.',
    shadowInterpretation: 'Movement for movement\'s sake isn\'t progress. Are you walking in circles? Sometimes you need to pause and get your bearings.',
    reflectionQuestion: 'What step have you been avoiding taking because the whole path isn\'t clear?',
    keywords: ['action', 'movement', 'courage', 'clarity', 'creation'],
    visualDescription: 'Footsteps creating a path as they go. Nothing ahead until it\'s walked. Adventurous, forward-moving energy.',
    affirmation: 'I create my path by taking the next step.'
  }
];

// ============================================
// INTEGRATION CARDS (6) - Where You Are in the Journey
// ============================================

const INTEGRATION_CARDS: OracleCard[] = [
  {
    id: 'integration-01-the-seed',
    name: 'The Seed',
    suit: 'integration',
    suitOrder: 1,
    coreMeaning: 'Something new is beginning. Honor the underground phase.',
    lightInterpretation: 'You\'ve planted something—an intention, a desire, a new chapter. It\'s underground now. You can\'t see growth yet, but that doesn\'t mean nothing\'s happening. Trust the dark.',
    shadowInterpretation: 'Are you digging up the seed to check if it\'s growing? Some things need to be left alone to develop. Stop checking, start trusting.',
    reflectionQuestion: 'What have you recently planted that needs time to grow unseen?',
    keywords: ['beginnings', 'potential', 'trust', 'patience', 'gestation'],
    visualDescription: 'A seed beneath dark soil, with the faintest hint of roots beginning. Above ground is anticipation, below is quiet work.',
    affirmation: 'I trust what\'s growing beneath the surface.'
  },
  {
    id: 'integration-02-the-breakthrough',
    name: 'The Breakthrough',
    suit: 'integration',
    suitOrder: 2,
    coreMeaning: 'The wall is thinner than you think. Push through.',
    lightInterpretation: 'You\'re closer than you realize. That resistance you\'re feeling? It often intensifies right before the breakthrough. One more push. One more try. The other side is waiting.',
    shadowInterpretation: 'Not every wall is meant to be broken through. Some are meant to redirect you. Is this resistance or redirection?',
    reflectionQuestion: 'What breakthrough is trying to happen in your life right now?',
    keywords: ['breakthrough', 'persistence', 'threshold', 'transformation', 'momentum'],
    visualDescription: 'Light breaking through a crack in stone. The moment of emergence. Dynamic energy. Triumphant but not aggressive.',
    affirmation: 'I am on the edge of a breakthrough.'
  },
  {
    id: 'integration-03-the-release',
    name: 'The Release',
    suit: 'integration',
    suitOrder: 3,
    coreMeaning: 'Let go. What you\'re gripping is already gone.',
    lightInterpretation: 'Something needs to be set down. A belief, a relationship, an old version of yourself, an expectation. Your hands are too full to receive what\'s coming. Release creates space.',
    shadowInterpretation: 'Are you releasing or avoiding? Sometimes "letting go" is spiritual bypass for not dealing with something. What actually needs processing here?',
    reflectionQuestion: 'What are you holding onto that\'s ready to be released?',
    keywords: ['release', 'surrender', 'letting go', 'freedom', 'space'],
    visualDescription: 'Hands opening, releasing something that transforms into light or birds as it leaves. Relief, not loss. Breath out.',
    affirmation: 'I release what no longer serves me with gratitude and grace.'
  },
  {
    id: 'integration-04-the-expansion',
    name: 'The Expansion',
    suit: 'integration',
    suitOrder: 4,
    coreMeaning: 'You\'re outgrowing your container. Let yourself stretch.',
    lightInterpretation: 'What used to fit doesn\'t anymore. Your life, your relationships, your self-concept—they\'re being asked to expand. It\'s uncomfortable. It\'s also exactly right.',
    shadowInterpretation: 'Expansion without integration leads to fragmentation. Are you growing too fast? Sometimes you need to pause and let your roots catch up.',
    reflectionQuestion: 'Where is life asking you to expand beyond your current limits?',
    keywords: ['expansion', 'growth', 'stretching', 'discomfort', 'evolution'],
    visualDescription: 'A shape pushing beyond its boundaries, becoming larger. Not explosive, but steady growth. Colors expanding and brightening.',
    affirmation: 'I am ready to expand into a bigger version of my life.'
  },
  {
    id: 'integration-05-evidence-is-coming',
    name: 'Evidence is Coming',
    suit: 'integration',
    suitOrder: 5,
    coreMeaning: 'Your transformation is real. The proof will catch up.',
    lightInterpretation: 'You\'ve changed inside but the outside doesn\'t reflect it yet. This is normal. Internal shifts precede external evidence. Keep going. The proof is on its way.',
    shadowInterpretation: 'Are you actually changing, or just hoping you are? If there\'s zero evidence over extended time, that\'s worth examining honestly.',
    reflectionQuestion: 'What internal shift have you made that\'s still waiting for external confirmation?',
    keywords: ['evidence', 'proof', 'manifestation', 'patience', 'faith'],
    visualDescription: 'Seeds underground with a forecast of the garden above. What\'s coming is beautiful. Trust imagery.',
    affirmation: 'Evidence of my transformation is on its way.'
  },
  {
    id: 'integration-06-the-new-beginning',
    name: 'The New Beginning',
    suit: 'integration',
    suitOrder: 6,
    coreMeaning: 'The next chapter starts now. Step forward.',
    lightInterpretation: 'Enough processing, preparing, planning. The new beginning is here. You don\'t need more information or more readiness. You need to start. The path becomes clear by walking it.',
    shadowInterpretation: 'New beginnings require released endings. Are you trying to start fresh while still carrying everything from before?',
    reflectionQuestion: 'What new beginning is asking you to step into it today?',
    keywords: ['beginning', 'fresh start', 'possibility', 'courage', 'initiation'],
    visualDescription: 'Dawn. First step onto a new path. Everything is possible. Clean, bright, inviting. Forward momentum.',
    affirmation: 'I embrace this new beginning with an open heart.'
  }
];

// ============================================
// COMPLETE DECK
// ============================================

export const ORACLE_DECK: OracleCard[] = [
  ...IDENTITY_CARDS,
  ...SHADOW_CARDS,
  ...DIRECTION_CARDS,
  ...INTEGRATION_CARDS
];

// Helper functions
export function getCardById(id: string): OracleCard | undefined {
  return ORACLE_DECK.find(card => card.id === id);
}

export function getCardsBySuit(suit: CardSuit): OracleCard[] {
  return ORACLE_DECK.filter(card => card.suit === suit);
}

export function getRandomCard(): OracleCard {
  return ORACLE_DECK[Math.floor(Math.random() * ORACLE_DECK.length)];
}

export function getRandomCards(count: number): OracleCard[] {
  const shuffled = [...ORACLE_DECK].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

export function getSuitDisplayName(suit: CardSuit): string {
  const names: Record<CardSuit, string> = {
    identity: 'Identity Cards',
    shadow: 'Shadow Cards',
    direction: 'Direction Cards',
    integration: 'Integration Cards'
  };
  return names[suit];
}

export function getSuitDescription(suit: CardSuit): string {
  const descriptions: Record<CardSuit, string> = {
    identity: 'Who you\'re becoming — cards about self-concept, worth, and personal power',
    shadow: 'What needs attention — cards illuminating patterns, blocks, and growth edges',
    direction: 'Guidance for your path — cards offering perspective, insight, and direction',
    integration: 'Where you are in the journey — cards about phases, timing, and transformation stages'
  };
  return descriptions[suit];
}
